import { useEffect, useState, useCallback, useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Server, Terminal, Power, RotateCcw, RefreshCw, ChevronRight, Users, Zap,
  Car, Trophy, Crosshair, Gamepad2, Bot, Loader2, ExternalLink,
  MessageSquare, CheckCircle2, AlertTriangle,
} from 'lucide-react'
import { pterodactylAPI, mapvetoAPI, teamsAPI, tournamentsAPI } from '@/lib/api'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import PageHeader from '@/components/PageHeader'
import WreckfestRegiePage from './WreckfestRegiePage'
import {
  CS16_MAPS, CS16_QUICK_CMDS, CS16_CVARS,
  Q3A_MAPS, Q3A_GAMEMODES, Q3A_QUICK_CMDS, Q3A_CVARS,
  MOHAA_MAPS, MOHAA_GAMEMODES, MOHAA_QUICK_CMDS, MOHAA_CVARS,
  UT2004_MAPS, UT2004_GAMETYPES, UT2004_QUICK_CMDS,
} from '@/lib/game-server-options'

/* Server-regie — één pagina voor alle game-servers.
   Links: server-selector (kaartjes met status-badge).
   Rechts: game-specifiek paneel dat kiest o.b.v. detectGameType(server).

   Wreckfest heeft z'n eigen volledige page (`/regie/wreckfest`) — we tonen
   hier alleen een intro-kaart met deep-link. Andere games krijgen inline
   panelen die uit generieke building-blocks bestaan (status, power, quick
   commands, bot-controls, free-form console-link). */

/* Sommige Pterodactyl-servers hebben geen expliciet game_type — leiden we af
   uit de naam. Zelfde logica als detectGameType() in backend gameServersController.js. */
function detectGameType(server) {
  if (server?.game_type) return server.game_type
  const s = (server?.name || '').toLowerCase()
  if (/wreckfest/.test(s)) return 'wreckfest'
  if (/cs\s?1\.6|counter.?strike\s?1|rehlds/.test(s)) return 'cs16'
  if (/mohaa|medal.of.honor/.test(s)) return 'mohaa'
  if (/quake\s?3|q3a/.test(s)) return 'q3a'
  if (/ut2004|unreal\s?tournament\s?2004/.test(s)) return 'ut2004'
  if (/tetrinet/.test(s)) return 'tetrinet'
  if (/factorio/.test(s)) return 'factorio'
  if (/openra|red\s?alert/.test(s)) return 'openra'
  if (/minecraft/.test(s)) return 'minecraft'
  if (/team\s?speak|ts3/.test(s)) return 'teamspeak'
  return 'other'
}

const ICON_BY_TYPE = {
  cs16: Crosshair, wreckfest: Car, mohaa: Gamepad2, q3a: Gamepad2,
  ut2004: Gamepad2, factorio: Gamepad2, openra: Gamepad2, minecraft: Gamepad2,
  teamspeak: Users, tetrinet: Gamepad2, other: Server,
}

const STATE_COLOR = {
  running:   'bg-live',
  starting:  'bg-yellow-400 animate-pulse',
  stopping:  'bg-yellow-400 animate-pulse',
  offline:   'bg-muted-foreground/60',
  unknown:   'bg-muted-foreground/60',
}

export default function ServerRegiePage() {
  const [servers, setServers] = useState([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy]       = useState(false)
  const [msg, setMsg]         = useState(null)
  const [err, setErr]         = useState(null)
  const [params, setParams]   = useSearchParams()
  const selectedId = params.get('server') || null

  const fetchServers = useCallback(async () => {
    try {
      const r = await pterodactylAPI.getServers(true).catch(() => ({ data: { servers: [] } }))
      setServers((r.data.servers || []).filter(s => !s.is_hidden))
    } catch (e) {
      setErr(e.response?.data?.error || e.message)
    } finally { setLoading(false) }
  }, [])

  useEffect(() => {
    fetchServers()
    const iv = setInterval(fetchServers, 5000)
    return () => clearInterval(iv)
  }, [fetchServers])

  const selected = useMemo(
    () => servers.find(s => s.identifier === selectedId) || null,
    [servers, selectedId]
  )

  // Auto-select eerste server als niks geselecteerd
  useEffect(() => {
    if (!selectedId && servers.length > 0) {
      setParams({ server: servers[0].identifier }, { replace: true })
    }
  }, [selectedId, servers, setParams])

  const power = async (signal) => {
    if (!selected) return
    setBusy(true); setMsg(null); setErr(null)
    try {
      await pterodactylAPI.sendPower(selected.identifier, signal)
      setMsg(`Signal '${signal}' verstuurd.`)
      setTimeout(fetchServers, 1500)
    } catch (e) {
      setErr(e.response?.data?.error || e.message)
    } finally { setBusy(false) }
  }
  const sendCmd = async (command) => {
    if (!selected || !command) return
    setBusy(true); setMsg(null); setErr(null)
    try {
      await pterodactylAPI.sendCommand(selected.identifier, command)
      setMsg(`Commando "${command}" verstuurd.`)
    } catch (e) {
      setErr(e.response?.data?.error || e.message)
    } finally { setBusy(false) }
  }

  return (
    <div className="container mx-auto p-6 space-y-4">
      <PageHeader icon={Server} title="Server-regie" />
      <p className="text-muted-foreground text-sm">
        Bediening voor alle game-servers op één plek. Kies links de server; rechts verschijnen game-specifieke bediening en snelknoppen.
      </p>

      {err && (
        <div className="rounded border border-signal/40 bg-signal/10 p-3 text-sm text-signal flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> {err}
        </div>
      )}
      {msg && (
        <div className="rounded border border-live/40 bg-live/10 p-3 text-sm text-live flex items-start gap-2">
          <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" /> {msg}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
        {/* Server-selector */}
        <ServerSelector servers={servers} loading={loading} selectedId={selectedId}
          onSelect={(id) => setParams({ server: id })} />

        {/* Paneel voor geselecteerde server */}
        <div>
          {!selected ? (
            <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">
              {loading ? 'Servers laden…' : 'Kies een server links.'}
            </CardContent></Card>
          ) : (
            <ServerPanel server={selected} busy={busy} onPower={power} onCmd={sendCmd} />
          )}
        </div>
      </div>
    </div>
  )
}

function ServerSelector({ servers, loading, selectedId, onSelect }) {
  if (loading && servers.length === 0) {
    return <Card><CardContent className="p-4 text-sm text-muted-foreground">Laden…</CardContent></Card>
  }
  return (
    <div className="space-y-1.5">
      {servers.map(s => {
        const gt = detectGameType(s)
        const Icon = ICON_BY_TYPE[gt] || Server
        const state = s.status || s.current_state || 'unknown'
        const active = s.identifier === selectedId
        return (
          <button key={s.identifier} onClick={() => onSelect(s.identifier)}
            className={`w-full text-left rounded border p-2.5 flex items-center gap-2.5 transition-colors ${
              active
                ? 'border-acid bg-acid/8'
                : 'border-border hover:border-acid/50 hover:bg-white/[.03]'
            }`}>
            <Icon className={`h-4 w-4 shrink-0 ${active ? 'text-acid' : 'text-muted-foreground'}`} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{s.name}</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-widest">{gt}</div>
            </div>
            <div className={`h-2 w-2 rounded-full ${STATE_COLOR[state] || STATE_COLOR.unknown}`}
              title={state} />
            {active && <ChevronRight className="h-4 w-4 text-acid" />}
          </button>
        )
      })}
    </div>
  )
}

function ServerPanel({ server, busy, onPower, onCmd }) {
  const type = detectGameType(server)

  // Wreckfest heeft z'n eigen volledige regie — inline zonder onze extra cards
  // (anders krijg je een dubbele status-header en dubbele console-kaart).
  if (type === 'wreckfest') return <WreckfestEmbedded />

  return (
    <div className="space-y-4">
      {/* Status + power */}
      <StatusHeader server={server} busy={busy} onPower={onPower} />

      {/* Game-specifieke panels */}
      {type === 'cs16'      && <Cs16Panel busy={busy} onCmd={onCmd} serverIdentifier={server.identifier} />}
      {(type === 'q3a' || type === 'mohaa') && <Q3StylePanel busy={busy} onCmd={onCmd} game={type} />}
      {type === 'ut2004'    && <Ut2004Panel busy={busy} onCmd={onCmd} />}

      {/* Free-form command console — voor alle games behalve wreckfest */}
      <QuickConsole server={server} busy={busy} onCmd={onCmd} />
    </div>
  )
}

function StatusHeader({ server, busy, onPower }) {
  const state = server.status || server.current_state || 'unknown'
  const mem   = server.resources?.memory_bytes ?? server.memory_bytes ?? null
  const memMb = mem != null ? Math.round(mem / 1024 / 1024) : null
  const players = server.resources?.players ?? server.players ?? null
  return (
    <Card>
      <CardContent className="p-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2 flex-1 min-w-[15rem]">
          <div className={`h-2.5 w-2.5 rounded-full ${STATE_COLOR[state] || STATE_COLOR.unknown}`} />
          <div className="min-w-0">
            <div className="font-semibold text-sm capitalize truncate">{server.name}</div>
            <div className="text-[10px] text-muted-foreground">
              {state}
              {memMb != null && ` · ${memMb} MB`}
              {players != null && ` · ${players} spelers`}
              {server.ip_address && ` · ${server.ip_address}:${server.port}`}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onPower('start')}>
            <Power className="h-3.5 w-3.5 mr-1" /> Start
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onPower('restart')}>
            <RotateCcw className="h-3.5 w-3.5 mr-1" /> Herstart
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onPower('stop')}>
            Stop
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

/* Wreckfest — hergebruikt de volledige WreckfestRegiePage inline (embedded).
   Zo krijg je hier meteen alle Wreckfest-features (config, event-loop, presets,
   log-tail, spectator-controls) zonder door te klikken. */
function WreckfestEmbedded() {
  return <WreckfestRegiePage embedded />
}

/* Gedeelde bouwblokken --------------------------------------- */

function MapSelectCard({ maps, mapCommand, busy, onCmd }) {
  const [selected, setSelected] = useState(maps[0])
  return (
    <Card><CardContent className="p-4 space-y-2">
      <h3 className="font-semibold text-sm uppercase tracking-widest text-muted-foreground">Map wisselen</h3>
      <div className="flex gap-2">
        <select className="flex-1 h-9 rounded-md border border-border bg-background px-2 text-sm"
          value={selected} onChange={e => setSelected(e.target.value)}>
          {maps.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <Button size="sm" disabled={busy} onClick={() => onCmd(mapCommand(selected))}>Wissel</Button>
      </div>
    </CardContent></Card>
  )
}

function QuickCmdsCard({ title, cmds, busy, onCmd }) {
  return (
    <Card><CardContent className="p-4 space-y-2">
      <h3 className="font-semibold text-sm uppercase tracking-widest text-muted-foreground flex items-center gap-2">
        <Zap className="h-3.5 w-3.5" /> {title}
      </h3>
      <div className="flex flex-wrap gap-2">
        {cmds.map(c => (
          <Button key={c.label} size="sm" variant="outline" disabled={busy} onClick={() => onCmd(c.cmd)}>
            {c.label}
          </Button>
        ))}
      </div>
    </CardContent></Card>
  )
}

/* CvarEditor — voor GoldSrc (`<key> <value>`) en Q3-style (`set <key> <value>`).
   commandBuilder(key, value) returnt het exact commando. */
function CvarEditorCard({ title, cvars, commandBuilder, busy, onCmd }) {
  const [draft, setDraft] = useState({})
  return (
    <Card><CardContent className="p-4 space-y-2">
      <h3 className="font-semibold text-sm uppercase tracking-widest text-muted-foreground">{title}</h3>
      <div className="grid gap-2 sm:grid-cols-2">
        {cvars.map(cv => (
          <div key={cv.key} className="flex items-end gap-2">
            <label className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                {cv.label} <code className="ml-1 text-muted-foreground/60">{cv.key}</code>
              </div>
              {cv.type === 'select' ? (
                <select className="w-full h-8 rounded border border-border bg-background px-2 text-sm"
                  value={draft[cv.key] ?? ''} onChange={e => setDraft(d => ({ ...d, [cv.key]: e.target.value }))}>
                  <option value="">–</option>
                  {cv.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : cv.type === 'bool' ? (
                <select className="w-full h-8 rounded border border-border bg-background px-2 text-sm"
                  value={draft[cv.key] ?? ''} onChange={e => setDraft(d => ({ ...d, [cv.key]: e.target.value }))}>
                  <option value="">–</option>
                  <option value="0">Uit (0)</option>
                  <option value="1">Aan (1)</option>
                </select>
              ) : (
                <Input type={cv.type} step={cv.step} value={draft[cv.key] ?? ''}
                  onChange={e => setDraft(d => ({ ...d, [cv.key]: e.target.value }))} className="h-8" />
              )}
            </label>
            <Button size="sm" variant="outline" disabled={busy || !draft[cv.key]?.toString().length}
              onClick={() => onCmd(commandBuilder(cv.key, draft[cv.key]))}>Zet</Button>
          </div>
        ))}
      </div>
    </CardContent></Card>
  )
}

function BotCard({ children }) { return <Card><CardContent className="p-4 space-y-3">{children}</CardContent></Card> }

/* Map-veto starter voor CS 1.6 — kies twee teams, backend maakt sessie,
   toont link die admin naar kapiteins deelt. */
function MapvetoCard({ serverIdentifier }) {
  const [teams, setTeams] = useState([])
  const [tournaments, setTournaments] = useState([])
  const [teamA, setTeamA] = useState('')
  const [teamB, setTeamB] = useState('')
  const [tournamentId, setTournamentId] = useState('')
  const [matches, setMatches] = useState([])
  const [matchId, setMatchId] = useState('')
  const [busy, setBusy] = useState(false)
  const [recent, setRecent] = useState([])
  const [feedback, setFeedback] = useState(null)

  const laad = async () => {
    try {
      const [t, r, tr] = await Promise.all([
        teamsAPI.list(),
        mapvetoAPI.recent(),
        tournamentsAPI.getTournaments({ status: 'active' }).catch(() =>
          tournamentsAPI.getTournaments()),
      ])
      setTeams(t.data?.teams || [])
      setRecent(r.data?.sessions || [])
      // Toon enkel toernooien die nog niet gedaan zijn.
      const list = tr.data?.tournaments || []
      setTournaments(list.filter(x => ['registration','in_progress'].includes(x.status)))
    } catch {}
  }
  useEffect(() => { laad() }, [])

  // Als tournament gekozen: haal matches (open + in_progress) op
  useEffect(() => {
    if (!tournamentId) { setMatches([]); setMatchId(''); return }
    tournamentsAPI.getTournament(tournamentId)
      .then(r => {
        const all = r.data?.matches || r.data?.tournament?.matches || []
        setMatches(all.filter(m => m.status !== 'completed'))
      })
      .catch(() => setMatches([]))
  }, [tournamentId])

  // Auto-fill team A/B als match gekozen wordt die persistent team-ids heeft
  useEffect(() => {
    if (!matchId) return
    const m = matches.find(x => String(x.id) === String(matchId))
    if (m?.participant1_team_id) setTeamA(String(m.participant1_team_id))
    if (m?.participant2_team_id) setTeamB(String(m.participant2_team_id))
  }, [matchId, matches])

  const start = async () => {
    if (!teamA || !teamB || teamA === teamB) {
      setFeedback({ type: 'err', text: 'Kies 2 verschillende teams' }); return
    }
    setBusy(true); setFeedback(null)
    try {
      const r = await mapvetoAPI.create({
        team_a_id: Number(teamA), team_b_id: Number(teamB),
        server_identifier: serverIdentifier, game: 'cs16',
        tournament_id: tournamentId ? Number(tournamentId) : undefined,
        match_id: matchId ? Number(matchId) : undefined,
      })
      const url = `${window.location.origin}/mapveto/${r.data.uuid}`
      try { await navigator.clipboard.writeText(url) } catch {}
      setFeedback({ type: 'ok', text: `Sessie gestart — link gekopieerd (${url})`, uuid: r.data.uuid })
      setTeamA(''); setTeamB(''); setTournamentId(''); setMatchId('')
      laad()
    } catch (e) {
      setFeedback({ type: 'err', text: e.response?.data?.error || e.message })
    } finally { setBusy(false) }
  }

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <h3 className="font-semibold text-sm uppercase tracking-widest text-muted-foreground flex items-center gap-2">
          <Crosshair className="h-3.5 w-3.5" /> Map-veto (BO1)
        </h3>
        {/* Optioneel: koppel aan toernooi + match zodat de gekozen map
            terug in de bracket-kaart wordt geschreven. */}
        <div className="grid gap-2 md:grid-cols-2">
          <select value={tournamentId} onChange={e => setTournamentId(e.target.value)}
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm">
            <option value="">— optioneel: toernooi —</option>
            {tournaments.map(t => (
              <option key={t.id} value={t.id}>{t.name} ({t.game})</option>
            ))}
          </select>
          <select value={matchId} onChange={e => setMatchId(e.target.value)}
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                  disabled={!tournamentId || matches.length === 0}>
            <option value="">— optioneel: match —</option>
            {matches.map(m => (
              <option key={m.id} value={m.id}>
                Ronde {m.round} · match {m.match_number}
                {m.bracket_type && m.bracket_type !== 'winners' ? ` · ${m.bracket_type}` : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="grid gap-2 md:grid-cols-[1fr_1fr_auto]">
          <select value={teamA} onChange={e => setTeamA(e.target.value)}
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm">
            <option value="">Team A…</option>
            {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <select value={teamB} onChange={e => setTeamB(e.target.value)}
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm">
            <option value="">Team B…</option>
            {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <Button size="sm" onClick={start} disabled={busy || !teamA || !teamB}>
            Start veto
          </Button>
        </div>
        {feedback && (
          <div className={`text-xs ${feedback.type === 'ok' ? 'text-live' : 'text-signal'}`}>
            {feedback.text}
            {feedback.uuid && (
              <Link to={`/mapveto/${feedback.uuid}`} className="ml-2 underline text-acid">
                Open sessie
              </Link>
            )}
          </div>
        )}
        <p className="text-[10px] text-muted-foreground">
          Beide kapiteins openen de link. Ze bannen om beurten. Bij 1 map over kan admin die
          via de "Load"-knop rechtstreeks op deze server laden.
        </p>
        {recent.length > 0 && (
          <div className="pt-2 border-t border-border/40">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
              Recente sessies
            </div>
            <div className="space-y-1">
              {recent.slice(0, 5).map(s => (
                <Link key={s.uuid} to={`/mapveto/${s.uuid}`}
                      className="flex items-center gap-2 text-xs px-2 py-1 rounded hover:bg-muted/30 no-underline">
                  <span className="font-mono text-[10px] text-muted-foreground shrink-0">
                    {new Date(s.created_at).toLocaleString('nl-BE', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}
                  </span>
                  <span className="truncate flex-1">
                    {s.team_a_name} vs {s.team_b_name}
                    {s.tournament_name && (
                      <span className="pf-mono text-[10px] text-muted-foreground ml-1">
                        · {s.tournament_name}
                        {s.match_round && ` R${s.match_round}·M${s.match_number}`}
                      </span>
                    )}
                  </span>
                  {s.winner_map
                    ? <span className="pf-mono text-[10px] text-live shrink-0">→ {s.winner_map}</span>
                    : <span className="pf-mono text-[10px] text-acid shrink-0">bezig</span>}
                </Link>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

/* CS 1.6 --------------------------------------- */
function Cs16Panel({ busy, onCmd, serverIdentifier }) {
  const [quota, setQuota] = useState('')
  return (
    <>
      <MapvetoCard serverIdentifier={serverIdentifier} />
      <MapSelectCard maps={CS16_MAPS} mapCommand={m => `changelevel ${m}`} busy={busy} onCmd={onCmd} />
      <QuickCmdsCard title="Snel-commando's" cmds={CS16_QUICK_CMDS} busy={busy} onCmd={onCmd} />
      <BotCard>
        <h3 className="font-semibold text-sm uppercase tracking-widest text-muted-foreground flex items-center gap-2">
          <Bot className="h-3.5 w-3.5" /> Bots (YaPB)
        </h3>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onCmd('yb addbot')}>+ Bot (auto team)</Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onCmd('yb addbot_ct')}>+ CT bot</Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onCmd('yb addbot_t')}>+ T bot</Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onCmd('yb kickbot')}>- Bot</Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onCmd('yb kickbots')}>Kick alle bots</Button>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Aantal bots (quota)</div>
            <Input type="number" min="0" max="32" value={quota} onChange={e => setQuota(e.target.value)} className="w-20" />
          </label>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onCmd(`yb_quota ${Number(quota)||0}`)}>Zet quota</Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onCmd('yb_quota_mode fill')}>Auto-fill aan</Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onCmd('yb_quota_mode normal')}>Auto-fill uit</Button>
        </div>
        <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-border/40">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground mr-1">Moeilijkheid</span>
          {[
            { v: 0, l: 'Newbie' },{ v: 1, l: 'Easy' },{ v: 2, l: 'Normal' },
            { v: 3, l: 'Hard' },  { v: 4, l: 'Expert' },
          ].map(d => (
            <Button key={d.v} size="sm" variant="outline" disabled={busy}
              onClick={() => onCmd(`yb_difficulty ${d.v}`)}>{d.l}</Button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground mr-1">Team-verdeling</span>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onCmd('yb_join_team any')}>Beide</Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onCmd('yb_join_team CT')}>Alleen CT</Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onCmd('yb_join_team T')}>Alleen T</Button>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground mr-1">Auto-kick bij real players</span>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onCmd('yb_autokill 1')}>Aan</Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onCmd('yb_autokill 0')}>Uit</Button>
        </div>
        <p className="text-[10px] text-muted-foreground">
          "Auto-fill aan" = bots vullen tot maxplayers. "Auto-kick" schopt automatisch een bot als
          een echte speler joint. YaPB moet als plugin geladen zijn (standaard bij de compo-egg).
        </p>
      </BotCard>
      <CvarEditorCard title="Server-cvars (mp_*)" cvars={CS16_CVARS}
        commandBuilder={(k, v) => `${k} ${v}`} busy={busy} onCmd={onCmd} />
    </>
  )
}

/* Q3A / MOHAA ---------------------------------- */
function Q3StylePanel({ busy, onCmd, game }) {
  const [botName, setBotName] = useState('Bot')
  const maps      = game === 'q3a' ? Q3A_MAPS       : MOHAA_MAPS
  const cvars     = game === 'q3a' ? Q3A_CVARS      : MOHAA_CVARS
  const quickCmds = game === 'q3a' ? Q3A_QUICK_CMDS : MOHAA_QUICK_CMDS
  const gamemodes = game === 'q3a' ? Q3A_GAMEMODES  : MOHAA_GAMEMODES
  return (
    <>
      <MapSelectCard maps={maps} mapCommand={m => `map ${m}`} busy={busy} onCmd={onCmd} />
      <QuickCmdsCard title="Snel-commando's" cmds={quickCmds} busy={busy} onCmd={onCmd} />
      <BotCard>
        <h3 className="font-semibold text-sm uppercase tracking-widest text-muted-foreground flex items-center gap-2">
          <Bot className="h-3.5 w-3.5" /> Bots
        </h3>
        <div className="flex flex-wrap gap-2 items-center">
          <Input value={botName} onChange={e => setBotName(e.target.value)}
            className="w-40" placeholder="Bot-naam" />
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onCmd(`addbot ${botName || 'Bot'} 3`)}>
            + Bot (skill 3)
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onCmd('kickbots')}>Kick alle bots</Button>
        </div>
      </BotCard>
      <div className="text-[10px] text-muted-foreground pl-1">
        Gametypes voor <code>set g_gametype</code>: {gamemodes.map(g => `${g.value}=${g.label}`).join(' · ')}
      </div>
      <CvarEditorCard title="Server-cvars" cvars={cvars}
        commandBuilder={(k, v) => `set ${k} "${v}"`} busy={busy} onCmd={onCmd} />
    </>
  )
}

/* UT2004 --------------------------------------- */
function Ut2004Panel({ busy, onCmd }) {
  const [selectedMap, setSelectedMap] = useState(UT2004_MAPS[0])
  const [selectedGT,  setSelectedGT]  = useState(UT2004_GAMETYPES[0].value)
  return (
    <>
      <Card><CardContent className="p-4 space-y-2">
        <h3 className="font-semibold text-sm uppercase tracking-widest text-muted-foreground">Map + gametype wisselen</h3>
        <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
          <select className="h-9 rounded-md border border-border bg-background px-2 text-sm"
            value={selectedMap} onChange={e => setSelectedMap(e.target.value)}>
            {UT2004_MAPS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <select className="h-9 rounded-md border border-border bg-background px-2 text-sm"
            value={selectedGT} onChange={e => setSelectedGT(e.target.value)}>
            {UT2004_GAMETYPES.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
          </select>
          <Button size="sm" disabled={busy}
            onClick={() => onCmd(`servertravel ${selectedMap}?game=${selectedGT}`)}>Wissel</Button>
        </div>
      </CardContent></Card>
      <QuickCmdsCard title="Snel-commando's" cmds={UT2004_QUICK_CMDS} busy={busy} onCmd={onCmd} />
      <Card><CardContent className="p-4 space-y-2">
        <h3 className="font-semibold text-sm uppercase tracking-widest text-muted-foreground flex items-center gap-2">
          <Zap className="h-3.5 w-3.5" /> WebAdmin
        </h3>
        <div className="flex flex-wrap gap-2">
          <Button asChild size="sm" variant="default">
            {/* WebAdmin draait op de Wings-host (extern subnet). URL komt uit
                de admin-settings zodat de LAN-locatie een ander IP mag hebben.
                Fallback via window.location zodat we niet naar een dood
                subnet linken; admin kan het echte IP in /admin instellen. */}
            <a href={`http://${(typeof window !== 'undefined' ? window.location.hostname : 'wings')}:8090/ServerAdmin/`}
               target="_blank" rel="noopener noreferrer">
              WebAdmin openen <ExternalLink className="h-3.5 w-3.5 ml-1" />
            </a>
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground">Login: <code>admin</code> / <code>Root666!</code></p>
      </CardContent></Card>
    </>
  )
}

/* Free-form RCON-console voor iedereen — met deep-link naar volledige page */
function QuickConsole({ server, busy, onCmd }) {
  const [cmd, setCmd] = useState('')
  const send = () => { if (cmd.trim()) { onCmd(cmd.trim()); setCmd('') } }
  return (
    <Card>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-sm uppercase tracking-widest text-muted-foreground flex-1 flex items-center gap-2">
            <Terminal className="h-3.5 w-3.5" /> Console
          </h3>
          <Button asChild size="sm" variant="ghost">
            <Link to={`/game-console?server=${server.identifier}`}>
              Volledige console <ExternalLink className="h-3.5 w-3.5 ml-1" />
            </Link>
          </Button>
        </div>
        <div className="flex gap-2">
          <Input value={cmd} onChange={e => setCmd(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') send() }}
            placeholder="Free-form RCON-commando (bv. status, changelevel de_dust2, ...)" />
          <Button size="sm" disabled={busy || !cmd.trim()} onClick={send}>
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <MessageSquare className="h-3.5 w-3.5 mr-1" />}
            Verstuur
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground">
          Commando gaat naar de server-stdin. Output verschijnt in de volledige console (link boven).
        </p>
      </CardContent>
    </Card>
  )
}
